import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ANALYTICS_EVENTS } from '~/constants/analytics'
import { useToast } from '~/design-system/atom/use-toast'
import {
  deleteTeamMembersRemoveById,
  getTeamMembersList,
  postTeamMembersInvite,
  putTeamMembersUpdateById,
} from '~/generated/backendProvider'
import { ANALYTICS } from '~/utils/tracking'

export const QUERY_KEYS = {
  TEAM_MEMBERS: 'TEAM_MEMBERS',
  TEAM_MEMBER_INVITE: 'TEAM_MEMBER_INVITE',
  TEAM_MEMBER_ROLE_UPDATE: 'TEAM_MEMBER_ROLE_UPDATE',
  TEAM_MEMBER_REMOVE: 'TEAM_MEMBER_REMOVE',
}

const useGetAllTeamMembers = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [QUERY_KEYS.TEAM_MEMBERS],
    queryFn: () => getTeamMembersList(),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  })

  return {
    data: data?.items || [],
    isLoading,
    isError,
  }
}

const useInviteTeamMemberMutation = () => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: postTeamMembersInvite,
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: 'Email invite was sent successfully',
      })
      ANALYTICS.track(ANALYTICS_EVENTS.TEAM_MEMBER_INVITED, {
        invited_user_email: data?.email,
        invited_user_role: data?.role,
        invited_user_name: data?.name,
        is_success: true,
      })
      queryClient.invalidateQueries([QUERY_KEYS.TEAM_MEMBERS])
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })

      ANALYTICS.track(ANALYTICS_EVENTS.TEAM_MEMBER_INVITED, {
        invited_user_email: '',
        invited_user_role: '',
        invited_user_name: '',
        is_success: false,
      })
    },
  })
}

const useModifyTeamMemberRoleMutation = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: putTeamMembersUpdateById,
    onSuccess: (data, variables) => {
      toast({
        title: 'Success',
        description: 'Team member role updated successfully.',
        duration: 5000,
      })
      queryClient.invalidateQueries([QUERY_KEYS.TEAM_MEMBERS])
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update team member role.',
        duration: 5000,
        variant: 'destructive',
      })
    },
  })
}

const useRemoveTeamMemberMutation = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: deleteTeamMembersRemoveById,
    onSuccess: (data, variables) => {
      toast({
        title: 'Success',
        description: 'Team member was removed successfully',
        duration: 5000,
      })
      queryClient.invalidateQueries([QUERY_KEYS.TEAM_MEMBERS])
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          description: error?.message || 'Failed to remove team member',
          variant: 'destructive',
          duration: 5000,
        })
      } else {
        toast({
          title: 'Error',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to remove team member',
          variant: 'destructive',
          duration: 5000,
        })
      }
    },
  })
}

export {
  useGetAllTeamMembers,
  useInviteTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useModifyTeamMemberRoleMutation,
}
